import posthog from 'posthog-js';
import { getStoredUtmParams } from '~/utils/utm';

class AnalyticsService {
  private isMobile(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    return (
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
  }

  capture(eventName: string, properties?: Record<string, any>) {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const utmParams = getStoredUtmParams();
      const eventProperties = {
        ...properties,
        device_type: this.isMobile() ? 'mobile' : 'desktop',
        ...utmParams,
      };
      posthog.capture(eventName, eventProperties);

      if (process.env.NODE_ENV === 'development') {
        console.log('Analytics event captured:', eventName, eventProperties);
      }
    } catch (error) {
      console.error('Failed to capture analytics event:', error);
    }
  }

  identify(userId: string, properties?: Record<string, any>) {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const utmParams = getStoredUtmParams();
      const userData = {
        ...properties,
        plan_type: properties?.plan_type || 'free',
        ...utmParams,
      };

      posthog.identify(userId, userData);

      if (process.env.NODE_ENV === 'development') {
        console.log('Identify captured:', userId, userData);
      }
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  setUserProperties(properties: Record<string, any>) {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      posthog.people.set(properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  pageView(pageName?: string) {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      posthog.capture('$pageview', {
        page_name: pageName,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
      });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  reset() {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      console.error('Failed to reset analytics session:', error);
    }
  }

  trackPaymentInitiated(plan: string) {
    this.capture(ANALYTICS_EVENTS.PAYMENT_INITIATED, {
      plan_name: plan,
      section: 'Payment',
    });
  }

  trackPaymentCompleted(plan: string) {
    this.capture(ANALYTICS_EVENTS.PAYMENT_COMPLETED, {
      plan_name: plan,
      section: 'Payment',
    });
  }

  trackPaymentFailed(plan: string, error: string) {
    this.capture(ANALYTICS_EVENTS.PAYMENT_FAILED, {
      plan_name: plan,
      error_message: error,
      section: 'Payment',
    });
  }

  getPostHogInstance() {
    return posthog;
  }
}

export const analytics = new AnalyticsService();

export { AnalyticsService };

export const ANALYTICS_EVENTS = {
  BUTTON_CLICKED: 'button_clicked',
  LINK_CLICKED: 'link_clicked',
  PAYMENT_INITIATED: 'payment_initiated',
  PAYMENT_COMPLETED: 'payment_completed',
  PAYMENT_FAILED: 'payment_failed',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
